import { inject, Injectable } from '@angular/core';
import { ODataEntitySetService, ODataServiceFactory } from 'angular-odata';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CustomerOrganisation, DuplicateCandidateDto, Gl, Laboratory, Organisation } from '@workspace-app/odatabackend/QualicoatBackendModel';
import { OrganisationType } from '@workspace-app/shared';
import { GenericOrganisationDataService } from './generic-organisation-data.service';

@Injectable({
  providedIn: 'root',
})
export class CustomerDataService extends GenericOrganisationDataService<CustomerOrganisation> {
  private factory = inject(ODataServiceFactory);

  private laboratoriesServices: ODataEntitySetService<Laboratory>;
  private glsServices: ODataEntitySetService<Gl>;
  private organisationsServices: ODataEntitySetService<Organisation>;

  constructor() {
    super();
    this.dataServices = this.factory.entitySet<CustomerOrganisation>(
      'CustomerOrganisations',
      'QualicoatBackend.Domain.Crm.OrganisationTypes.CustomerOrganisation'
    );
    this.expandedString = 'Contacts,Contacts($orderby=FirstName),Addresses,AssignedGl,AssignedLaboratory,AssignedInspectionBody,ParentOrganisation,ProductionSites($expand=CoatingLines,Licenses,CertifiableSystems)';
    this.laboratoriesServices = this.factory.entitySet<Laboratory>(
      'Laboratories',
      'QualicoatBackend.Domain.Crm.OrganisationTypes.Laboratory'
    );
    this.glsServices = this.factory.entitySet<Gl>('Gls', 'QualicoatBackend.Domain.Crm.OrganisationTypes.Gl');
    this.organisationsServices = this.factory.entitySet<Organisation>(
      'Organisations',
      'QualicoatBackend.Domain.Crm.Organisation'
    );
  }

  fetch(organisationId: string): Observable<CustomerOrganisation> {
    return this.dataServices.entities().query((q) => {
      q.expand(this.expandedString);
    })
      .entity(organisationId)
      .fetch()
      .pipe(map((oDataEntity) => oDataEntity.entity!));
  }

  createCustomerOrganisation(customer: Partial<CustomerOrganisation>): Observable<string> {
    const customerForCreate: { [key: string]: unknown } = {};
    Object.entries(customer).forEach(([key, val]) => {
      if (val && val !== '') {
        customerForCreate[key] = val;
      }
    });
    switch (customer.organisationType) {
      case OrganisationType.Coater:
        return this.httpClient.post<string>(`/api/customers/coaters`, customerForCreate);
      case OrganisationType.Manufacturer:
      case OrganisationType.PowderManufacturer:
      case OrganisationType.ChemicalManufacturer:
      case OrganisationType.FilmSupplier:
        return this.httpClient.post<string>(`/api/customers/manufacturers`, customerForCreate);
      default:
        return new Observable((subscriber) => {
          subscriber.error('Invalid organisation type');
        });
    }
  }

  updateCustomer(organisationId: string, patch: Partial<CustomerOrganisation>): Observable<CustomerOrganisation> {
    return this.httpClient.put<CustomerOrganisation>(`/api/customers/${organisationId}`, patch);
  }

  fetchOrganisationsForAssignedGlSelect(): Observable<Organisation[]> {
    return this.glsServices.entities()
      ?.query((q) => {
        // Filter out QCT organizations - only allow GL type
        q.filter("organisationType eq 'GL'");
        q.orderBy('organisationType desc, name asc');
        q.top(100);
        q.skip(0);
      })
      .fetch()
      .pipe(map(({ entities }) => entities || []));
  }

  fetchOrganisationsForAssignedLaboratorySelect(glId: string): Observable<Laboratory[]> {
    return this.laboratoriesServices.entities()
      .query((q) => {
        q.filter(`assignedGls/any(gl: gl/Id eq ${glId})`);
        q.orderBy('organisationType desc, name asc');
        q.top(100);
        q.skip(0);
      })
      .fetch()
      .pipe(map(({ entities }) => entities || []));
  }

  fetchAllLaboratoriesForSelect(): Observable<Laboratory[]> {
    return this.laboratoriesServices.entities()
      .query((q) => {
        q.filter('qualicoat eq true');
        q.orderBy('name asc');
        q.top(100);
        q.skip(0);
      })
      .fetch()
      .pipe(map(({ entities }) => entities || []));
  }

  fetchInspectionBodiesForSelect(): Observable<Organisation[]> {
    // Inspection bodies are: GLs with isInspectionBody=true AND all laboratories
    const glsWithFlag$ = this.glsServices.entities()
      .query((q) => {
        q.filter('isInspectionBody eq true');
        q.orderBy('name asc');
        q.top(100);
        q.skip(0);
      })
      .fetch()
      .pipe(map(({ entities }) => entities || []));

    const allLaboratories$ = this.laboratoriesServices.entities()
      .query((q) => {
        q.orderBy('name asc');
        q.top(100);
        q.skip(0);
      })
      .fetch()
      .pipe(map(({ entities }) => entities || []));

    return forkJoin([glsWithFlag$, allLaboratories$]).pipe(
      map(([gls, labs]) => {
        // Combine and sort by name
        const combined: Organisation[] = [...gls, ...labs];
        return combined.sort((a, b) => a.name.localeCompare(b.name));
      })
    );
  }

  fetchMoveTargetCandidates(
    organisationType: string,
    countryName: string,
    excludeId: string
  ): Observable<CustomerOrganisation[]> {
    const escape = (value: string) => value.replace(/'/g, "''");
    return this.dataServices!.entities()
      .query((q) => {
        q.select(['id', 'name', 'organisationType', 'countryName']);
        // Moves are only allowed within the same country
        q.filter(
          `organisationType eq '${escape(organisationType)}' and countryName eq '${escape(countryName)}' and id ne ${excludeId}`
        );
        q.orderBy('name asc');
      })
      .fetch()
      .pipe(map(({ entities }) => entities || []));
  }

  fetchPotentialParentOrganisations(excludeId?: string): Observable<Organisation[]> {
    return this.organisationsServices.entities()
      .query((q) => {
        q.select(['id', 'name', 'organisationType']);
        // Only include customer organisations (Coater, Manufacturer, PowderManufacturer, ChemicalManufacturer)
        let filterStr = `(organisationType eq 'Coater' or organisationType eq 'Manufacturer' or organisationType eq 'PowderManufacturer' or organisationType eq 'ChemicalManufacturer')`;
        if (excludeId) {
          filterStr += ` and id ne ${excludeId}`;
        }
        q.filter(filterStr);
        q.orderBy('name asc');
      })
      .fetch()
      .pipe(map(({ entities }) => entities || []));
  }

  checkForDuplicates(organisationId: string, threshold: number = 85): Observable<DuplicateCandidateDto | null> {
    // Use angular-odata's function support for entity-specific functions
    const entity = this.dataServices!.entity(organisationId);
    const func = entity.function<{ threshold: number }, DuplicateCandidateDto>('CheckForDuplicates');
    return func.callEntity({ threshold });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- missing proper typing for CUD operations
  mergeOrganisations(survivorId: string, loserId: string): Observable<any> {
    // This is a REST endpoint, not OData, so we use HttpClient directly
    const params = {
      survivorId,
      loserId,
    };
    return this.httpClient.post('/api/organisations/merge', params);
  }
}
